import { PopoverPortalContext } from "@circle-react-shared/uikit/PopoverPortal";
import { useAddTipTapLinkModal } from "@circle-react-shared/uikit/TipTapBlockEditor/AddTipTapLinkModal";
import { LazyChatThreadsList } from "@circle-react/components/Chat/ChatThreads/LazyChatThreadsList";
import { CommunityFeatureFlagsContextProvider } from "@circle-react/contexts/communityFeatureFlagsContext";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { useUnreadChatThreads } from "@circle-react/hooks/chatsV2/useUnreadChatThreads";
import { Popover as CompassPopover } from "@circleco/compass/components/Popover";
import { useLocation } from "@tanstack/react-router";
import type { ComponentPropsWithoutRef } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { t } from "@/i18n-js/instance";

import { HeaderIconButton } from "../HeaderIconButton";
import { formatNotificationsCount } from "../utils";
import { Header } from "./Header";

type PopoverOpenChangeDetails = Parameters<
  NonNullable<ComponentPropsWithoutRef<typeof CompassPopover>["onOpenChange"]>
>[1];

export const ChatThreads = () => {
  const { currentCommunityMember } = usePunditUserContext();
  const { unreadThreadsCount } = useUnreadChatThreads();
  const [isOpen, setIsOpen] = useState(false);
  const { visible: isAddLinkModalOpen } = useAddTipTapLinkModal();
  const location = useLocation();
  const popoverPortalContextValue = useMemo(
    () => ({ onClose: () => setIsOpen(false) }),
    [],
  );

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const handleOpenChange = useCallback(
    (open: boolean, eventDetails: PopoverOpenChangeDetails) => {
      // The thread composer's add-link modal renders in a portal at the app
      // root, so pressing or focusing it reads as an interaction outside this
      // popover. Closing here would unmount the composer and the modal with it.
      const isDismissedFromOutside =
        eventDetails.reason === "outside-press" ||
        eventDetails.reason === "focus-out";

      if (!open && isDismissedFromOutside && isAddLinkModalOpen) {
        eventDetails.cancel();
        return;
      }

      setIsOpen(open);
    },
    [isAddLinkModalOpen],
  );

  const shouldDisplayChatThreads =
    currentCommunityMember?.policies?.can_access_threads_tab ||
    unreadThreadsCount > 0;

  if (!shouldDisplayChatThreads) {
    return null;
  }

  return (
    <CommunityFeatureFlagsContextProvider>
      <CompassPopover
        trigger={
          <HeaderIconButton
            icon="message-dots"
            ariaLabel={t("header_v3.chat_threads")}
            hotkey="alt+t"
            counter={
              unreadThreadsCount > 0
                ? formatNotificationsCount(unreadThreadsCount)
                : undefined
            }
            isOpen={isOpen}
            data-testid="chat-threads-popover-button"
          />
        }
        open={isOpen}
        onOpenChange={handleOpenChange}
        side="bottom"
        align="end"
        sideOffset={4}
        fillAvailableHeight
        className="size-full h-[37rem] w-[30rem] p-0"
      >
        <PopoverPortalContext.Provider value={popoverPortalContextValue}>
          <div
            className="flex size-full flex-col"
            data-testid="chat-threads-drawer"
          >
            <Header />
            <div
              className="min-h-0 flex-1 overflow-y-auto rounded-b-md bg-secondary dark:bg-primary p-3"
              id="chat-threads-header"
            >
              <LazyChatThreadsList isRenderedOnHeader />
            </div>
          </div>
        </PopoverPortalContext.Provider>
      </CompassPopover>
    </CommunityFeatureFlagsContextProvider>
  );
};
